import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  ClipboardList, Search, ChevronRight, X, Mail, Phone, Building2,
  MapPin, CheckCircle2, Clock, Target, AlertTriangle, Monitor, Trash2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Labels for display
const BUSINESS_TYPE_LABELS = {
  restaurant: 'Restaurant',
  cafe: 'Café',
  bar: 'Bar',
  fast_food: 'Schnellrestaurant / Imbiss',
  franchise: 'Systemgastronomie / Franchise',
  catering: 'Event- oder Cateringbetrieb',
  hotel: 'Hotel mit Gastronomie',
  other: 'Anderes'
};

const LOCATION_COUNT_LABELS = {
  '1': '1 Standort',
  '2-3': '2-3 Standorte',
  '4-9': '4-9 Standorte',
  '10+': '10 oder mehr Standorte'
};

const SYSTEM_LABELS = {
  pos_payment: 'Kassensystem & Kartenzahlung',
  reservation: 'Reservierungssysteme',
  staff_planning: 'Personal- und Schichtplanung',
  smart_order: 'Smarte Order-Lösungen',
  delivery: 'Delivery-Integration',
  loyalty: 'Loyalty & Kundenbindung',
  optimization: 'Digitale Betriebsoptimierung',
  social_media: 'Social Media',
  website_reviews: 'Website- & Bewertungsmanagement'
};

const CHALLENGE_LABELS = {
  too_many_systems: 'Zu viele verschiedene Systeme',
  bad_integration: 'Schlechte Integration zwischen Tools',
  training: 'Aufwändige Schulung der Mitarbeiter',
  no_support: 'Mangelnder Support / kein Ansprechpartner',
  no_overview: 'Fehlender Überblick über Verträge & Kosten',
  duplicate_data: 'Unklare Daten oder doppelte Eingaben',
  no_time: 'Zu wenig Zeit für technische Themen',
  other: 'Anderes'
};

const GOAL_LABELS = {
  new_systems: 'Neue Systemempfehlungen',
  better_support: 'Besseren Support für bestehende Tools',
  overview: 'Gesamtübersicht / System-Landschaft',
  onboarding: 'Unterstützung bei Einführung / Schulung'
};

const AdminConsultations = () => {
  const [consultations, setConsultations] = useState([]);
  const [filteredConsultations, setFilteredConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    fetchConsultations();
  }, []);

  useEffect(() => {
    let filtered = consultations;
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }
    
    // Filter by search
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.contact_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredConsultations(filtered);
  }, [searchTerm, statusFilter, consultations]);

  const fetchConsultations = async () => {
    try {
      const response = await axios.get(`${API}/admin/consultations`);
      setConsultations(response.data);
      setFilteredConsultations(response.data);
    } catch (error) {
      console.error('Failed to fetch consultations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-none">Abgeschlossen</span>;
      case 'in_progress':
        return <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-none">In Bearbeitung</span>;
      case 'contacted':
        return <span className="px-2 py-1 text-xs font-medium bg-lilac text-ink rounded-none">Kontaktiert</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-off text-ink/80 rounded-none">{status}</span>;
    }
  };

  const openDetail = (consultation) => {
    setSelectedConsultation(consultation);
    setDetailModalOpen(true);
  };

  const handleDeleteClick = (consultationId, e) => {
    e.stopPropagation();
    setDeleteConfirmId(consultationId);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    setDeletingId(deleteConfirmId);
    try {
      await axios.delete(`${API}/admin/consultations/${deleteConfirmId}`);
      setDeleteConfirmId(null);
      // If detail modal is open for this consultation, close it
      if (selectedConsultation?.id === deleteConfirmId) {
        setDetailModalOpen(false);
        setSelectedConsultation(null);
      }
      fetchConsultations();
    } catch (error) {
      console.error('Failed to delete consultation:', error);
      alert(error.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteFromModal = () => {
    if (selectedConsultation) {
      setDeleteConfirmId(selectedConsultation.id);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-lilac/50 rounded-none w-1/3"></div>
          <div className="h-64 bg-lilac/50 rounded-none"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-consultations">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-ink">Erstberatungen</h1>
          <p className="text-ink/80 mt-1">{consultations.length} Beratungsanfragen</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink/60" />
          <Input
            type="text"
            placeholder="Suchen nach Name oder E-Mail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 rounded-none"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('all')}
            className={statusFilter === 'all' ? 'bg-purple' : ''}
            size="sm"
          >
            Alle
          </Button>
          <Button
            variant={statusFilter === 'completed' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('completed')}
            className={statusFilter === 'completed' ? 'bg-green-600' : ''}
            size="sm"
          >
            Abgeschlossen
          </Button>
          <Button
            variant={statusFilter === 'in_progress' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('in_progress')}
            className={statusFilter === 'in_progress' ? 'bg-amber-600' : ''}
            size="sm"
          >
            In Bearbeitung
          </Button>
        </div>
      </div>

      {/* Consultations List */}
      <div className="bg-white rounded-none border-2 border-ink overflow-hidden">
        {filteredConsultations.length === 0 ? (
          <div className="p-8 text-center text-ink/60">
            {searchTerm || statusFilter !== 'all' ? 'Keine Erstberatungen gefunden' : 'Noch keine Erstberatungen vorhanden'}
          </div>
        ) : (
          <div className="divide-y divide-ink/15">
            {filteredConsultations.map((consultation) => (
              <div
                key={consultation.id}
                role="button"
                tabIndex={0}
                onClick={() => openDetail(consultation)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(consultation); } }}
                className="w-full p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-off transition-colors text-left cursor-pointer"
                data-testid={`consultation-row-${consultation.id}`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 bg-purple/10 rounded-none flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-purple" />
                  </div>
                  <div>
                    <p className="font-medium text-ink">
                      {consultation.contact_name || consultation.email}
                    </p>
                    <p className="text-sm text-ink/60">
                      {consultation.business_types?.map(t => BUSINESS_TYPE_LABELS[t] || t).join(', ') || 'Keine Angabe'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap sm:justify-end">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm text-ink/80">{consultation.email}</p>
                    <p className="text-xs text-ink/60">
                      {new Date(consultation.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                  {getStatusBadge(consultation.status)}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDeleteClick(consultation.id, e)}
                    className="text-ink/60 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <ChevronRight className="w-5 h-5 text-ink/60" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailModalOpen && selectedConsultation && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-bold text-ink">
                  Erstberatung: {selectedConsultation.contact_name || 'Unbekannt'}
                </h2>
                <p className="text-ink/60">{selectedConsultation.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteFromModal}
                  className="btn--danger"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Löschen
                </Button>
                <button onClick={() => setDetailModalOpen(false)} className="text-ink/60 hover:text-ink/80">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status & Contact */}
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedConsultation.status)}
                <span className="text-sm text-ink/60">
                  {new Date(selectedConsultation.created_at).toLocaleString('de-DE')}
                </span>
              </div>

              {/* Contact Info */}
              <div className="p-4 bg-off rounded-none space-y-2">
                <h3 className="font-semibold text-ink mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Kontaktdaten
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-ink/60">Name:</span>
                    <span className="text-ink ml-2">{selectedConsultation.contact_name || '-'}</span>
                  </div>
                  <div>
                    <span className="text-ink/60">E-Mail:</span>
                    <span className="text-ink ml-2">{selectedConsultation.email}</span>
                  </div>
                  <div>
                    <span className="text-ink/60">Telefon:</span>
                    <span className="text-ink ml-2">{selectedConsultation.contact_phone || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Business Type */}
              <div className="p-4 bg-off rounded-none">
                <h3 className="font-semibold text-ink mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Betriebsart
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedConsultation.business_types?.map((type, i) => (
                    <span key={i} className="px-3 py-1 bg-white border-2 border-ink rounded-none text-sm">
                      {BUSINESS_TYPE_LABELS[type] || type}
                    </span>
                  )) || <span className="text-ink/60">Keine Angabe</span>}
                </div>
                {selectedConsultation.other_business_type && (
                  <p className="mt-2 text-sm text-ink/80">
                    Sonstiges: {selectedConsultation.other_business_type}
                  </p>
                )}
              </div>

              {/* Location Count */}
              <div className="p-4 bg-off rounded-none">
                <h3 className="font-semibold text-ink mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Standorte
                </h3>
                <span className="px-3 py-1 bg-white border-2 border-ink rounded-none text-sm">
                  {LOCATION_COUNT_LABELS[selectedConsultation.location_count] || selectedConsultation.location_count || 'Keine Angabe'}
                </span>
              </div>

              {/* Current Systems */}
              <div className="p-4 bg-off rounded-none">
                <h3 className="font-semibold text-ink mb-3 flex items-center gap-2">
                  <Monitor className="w-4 h-4" />
                  Aktuelle Systeme
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedConsultation.current_systems?.length > 0 ? (
                    selectedConsultation.current_systems.map((system, i) => (
                      <span key={i} className="px-3 py-1 bg-green-50 border border-green-200 text-green-700 rounded-none text-sm">
                        {SYSTEM_LABELS[system] || system}
                      </span>
                    ))
                  ) : (
                    <span className="text-ink/60">Keine Systeme angegeben</span>
                  )}
                </div>
              </div>

              {/* Challenges */}
              <div className="p-4 bg-off rounded-none">
                <h3 className="font-semibold text-ink mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Herausforderungen
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedConsultation.challenges?.map((challenge, i) => (
                    <span key={i} className="px-3 py-1 bg-amber-50 border border-amber-200 text-amber-700 rounded-none text-sm">
                      {CHALLENGE_LABELS[challenge] || challenge}
                    </span>
                  )) || <span className="text-ink/60">Keine Angabe</span>}
                </div>
                {selectedConsultation.other_challenge && (
                  <p className="mt-2 text-sm text-ink/80">
                    Sonstiges: {selectedConsultation.other_challenge}
                  </p>
                )}
              </div>

              {/* Main Goal */}
              <div className="p-4 bg-off rounded-none">
                <h3 className="font-semibold text-ink mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Hauptziel
                </h3>
                <span className="px-3 py-1 bg-purple/10 text-purple rounded-none text-sm font-medium">
                  {GOAL_LABELS[selectedConsultation.main_goal] || selectedConsultation.main_goal || 'Keine Angabe'}
                </span>
              </div>

              {/* Special Requirements */}
              {selectedConsultation.special_requirements && (
                <div className="p-4 bg-off rounded-none">
                  <h3 className="font-semibold text-ink mb-3">Spezielle Anforderungen</h3>
                  <p className="text-ink/80 whitespace-pre-wrap">{selectedConsultation.special_requirements}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-ink/70 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-md w-full">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between">
              <h2 className="text-xl font-bold text-ink">Erstberatung löschen</h2>
              <button onClick={() => setDeleteConfirmId(null)} className="text-ink/60 hover:text-ink/80">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-none flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-ink">
                    {consultations.find(c => c.id === deleteConfirmId)?.contact_name || 'Unbekannt'}
                  </p>
                  <p className="text-sm text-ink/60">
                    {consultations.find(c => c.id === deleteConfirmId)?.email}
                  </p>
                </div>
              </div>
              <p className="text-ink/80 bg-red-50 p-4 rounded-none border border-red-100">
                <strong>Achtung:</strong> Diese Erstberatung wird unwiderruflich gelöscht. 
                Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
            </div>
            
            <div className="p-6 border-t border-ink/15 flex gap-3">
              <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="flex-1 rounded-none">
                Abbrechen
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={deletingId}
                className="btn--danger flex-1"
              >
                {deletingId ? 'Wird gelöscht...' : 'Endgültig löschen'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminConsultations;
